// src/pages/accounting/accounts/GeneralSalesAccountList.jsx
import React, { useEffect, useState } from "react";
import api from "../../../api";
import styles from "./GeneralSalesAccountList.module.css";

export default function GeneralSalesAccountList() {
  const [tree, setTree] = useState({});
  const [expanded, setExpanded] = useState({});
  const [partnerData, setPartnerData] = useState({});

  // ✅ 페이지 제목 및 탭 타이틀 설정
  useEffect(() => {
    document.title = "(일반) 매출 계정과목 목록";
  }, []); // eslint-disable-line

  // ✅ 계정과목 + 거래처 데이터 불러오기
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("accounting/accounts/general-sales/list/");
        const list = Array.isArray(data?.results) ? data.results : data;

        // ✅ 1️⃣ 계정과목 계층화 (sangse 추가)
        const grouped = {};
        list.forEach((item) => {
          const { gwang, hang, sehang, mok, semok, detail, sangse } = item;  // ✅ sangse 추가
          if (!grouped[gwang]) grouped[gwang] = {};
          if (!grouped[gwang][hang]) grouped[gwang][hang] = {};
          if (!grouped[gwang][hang][sehang]) grouped[gwang][hang][sehang] = {};
          if (!grouped[gwang][hang][sehang][mok]) grouped[gwang][hang][sehang][mok] = {};
          if (!grouped[gwang][hang][sehang][mok][semok])
            grouped[gwang][hang][sehang][mok][semok] = {};  // ✅ 배열 → 객체로 변경

          // ✅ detail 레벨 생성
          if (detail && !grouped[gwang][hang][sehang][mok][semok][detail]) {
            grouped[gwang][hang][sehang][mok][semok][detail] = [];
          }

          // ✅ sangse가 있으면 배열에 추가
          if (detail && sangse && !grouped[gwang][hang][sehang][mok][semok][detail].includes(sangse)) {
            grouped[gwang][hang][sehang][mok][semok][detail].push(sangse);
          }
        });

        // ✅ 2️⃣ 거래처 데이터 미리 조회
        const partnerDict = {};
        for (const gwang of Object.keys(grouped)) {
          for (const hang of Object.keys(grouped[gwang])) {
            for (const sehang of Object.keys(grouped[gwang][hang])) {
              for (const mok of Object.keys(grouped[gwang][hang][sehang])) {
                for (const semok of Object.keys(grouped[gwang][hang][sehang][mok])) {

                  const details = grouped[gwang][hang][sehang][mok][semok];
                  if (typeof details === "object" && !Array.isArray(details)) {  // ✅ 객체 체크로 변경
                    for (const d of Object.keys(details)) {  // ✅ Object.keys() 사용

                      try {
                        let data = [];
                        
                        if (d === "소독") {
                          // ✅ 환경/소독
                          const res = await api.get("/income-drafts/", {
                            params: { category: "환경/소독", subcategory: "소독방역" },
                          });
                          const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                          const partners = list
                            .map((r) => r?.partner?.name || r?.partner_name)
                            .filter(Boolean);
                        
                          data = [...new Set(partners)].map((name, idx) => ({
                            id: idx + 1,
                            name,
                          }));
                        
                        } else if (
                          // ✅ 환경/종합 (냉난방기, 승차대, 간판, 모래, 기타)
                          ["냉난방기 종합세척", "승차대 청소", "간판 청소", "모래 소독", "기타"].includes(d)
                        ) {
                          // ✅ DB subcategory 이름 매핑
                          const subcategoryMap = {
                            "냉난방기 종합세척": "냉난방기 종합세척",
                            "승차대 청소": "승차대 청소",
                            "간판 청소": "간판 청소",
                            "모래 소독": "모래 소독", // ✅ 수정: DB와 동일하게 맞춤
                          };
                          const subKey = subcategoryMap[d] || d;
                        
                          const res = await api.get("/env-total/", {
                            params: { category: "환경/종합", subcategory: subKey },
                          });
                          const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                        
                          const partners = list
                            .map(
                              (r) =>
                                r?.partner?.name ||
                                r?.partner_name ||
                                r?.vendor?.name ||
                                r?.vendor_name
                            )
                            .filter(Boolean);
                        
                          if (partners.length > 0) {
                            data = [...new Set(partners)].map((name, idx) => ({
                              id: idx + 1,
                              name,
                            }));
                          } else {
                            partnerDict[d] = [];
                          }
                        
                        } else {
                          // ✅ 나머지 detail 일반 처리
                          data = [];
                        }
                        
                        
                        partnerDict[d] = data;

                      } catch (err) {
                        console.error(`❌ 거래처(${d}) 로드 실패:`, err);
                        partnerDict[d] = [];
                      }
                    }
                  }
                }
              }
            }
          }
        }

        // ✅ 3️⃣ 숫자 기준으로 정렬
        const sortedTree = sortTree(grouped);

        // ✅ 상태 저장
        setTree(sortedTree);
        setPartnerData(partnerDict);

        // ✅ 기본 펼치기 전부 true
        const expandAll = {};
        const expandRecursively = (obj, prefix = "") => {
          Object.entries(obj).forEach(([key, val]) => {
            const id = `${prefix}${key}`;
            expandAll[id] = true;
            if (typeof val === "object" && !Array.isArray(val)) {
              expandRecursively(val, `${id}-`);
            }
          });
        };
        expandRecursively(sortedTree);
        setExpanded(expandAll);

        console.log("✅ 모든 거래처 및 계정과목 트리 로드 완료");
      } catch (err) {
        console.error("❌ 계정과목 불러오기 실패:", err);
      }
    })();
  }, []);

  // ✅ 숫자 우선 정렬 함수
  const sortKeysNumerically = (obj) => {
    const keys = Object.keys(obj);
    return keys.sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)?.[0] || 0, 10);
      const numB = parseInt(b.match(/\d+/)?.[0] || 0, 10);
      if (numA !== numB) return numA - numB;
      return a.localeCompare(b, "ko"); // 숫자 같으면 한글순
    });
  };

  const sortTree = (obj) => {
    const sorted = {};
    const sortedKeys = sortKeysNumerically(obj);
    for (const key of sortedKeys) {
      if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        sorted[key] = sortTree(obj[key]);
      } else {
        sorted[key] = obj[key];
      }
    }
    return sorted;
  };

  // ✅ detail 클릭 시
  const handleDetailClick = (detail) => {
    setExpanded((prev) => ({ ...prev, [detail]: !prev[detail] }));
  };

  // ✅ 트리 렌더링
  const renderTree = (node, level = 1, prefix = "") => {
    if (!node) return null;

    // 배열(sangse) - detail의 하위로 표시
    if (Array.isArray(node)) {
      return node.length > 0 ? (
        node.map((s, i) => (
          <div key={`${prefix}-s-${i}`} className={`${styles.row} ${styles[`level${level}`]}`}>
            🔸 {s}
          </div>
        ))
      ) : null;  // ✅ sangse가 없으면 아무것도 표시하지 않음
    }

    // 객체(하위 계층)
    return sortKeysNumerically(node).map((key) => {
      const value = node[key];
      const id = `${prefix}${key}`;
      
      // ✅ detail 레벨인지 확인 (value가 배열이면 sangse를 포함)
      const isDetailLevel = Array.isArray(value);
      
      if (isDetailLevel) {
        // ✅ detail 레벨: 거래처 표시
        const count = partnerData[key]?.length ?? 0;
        return (
          <div key={id}>
            <div
              className={`${styles.row} ${styles[`level${level}`]}`}
              onClick={() => handleDetailClick(key)}
            >
              🔹 {key}
              <span style={{ color: "#2563eb", fontSize: "13px", marginLeft: 4 }}>
                (거래처 {count}개소)
              </span>
            </div>

            {/* 거래처 목록 */}
            {expanded[key] && partnerData[key] && (
              <div className={styles.partnerBox}>
                {partnerData[key].length > 0 ? (
                  partnerData[key].map((p) => (
                    <div key={p.id} className={styles.partnerRow}>
                      🏢 {p.name}
                    </div>
                  ))
                ) : (
                  <div className={styles.empty}>거래처 없음</div>
                )}
              </div>
            )}

            {/* ✅ sangse 표시 (있을 경우에만) */}
            {expanded[key] && value && value.length > 0 && (
              <div className={styles.child}>
                {renderTree(value, level + 1, `${id}-`)}
              </div>
            )}
          </div>
        );
      }

      // ✅ 일반 계층 (gwang, hang, sehang, mok, semok)
      const hasChildren = typeof value === "object" && Object.keys(value).length > 0;

      return (
        <div key={id}>
          <div
            className={`${styles.row} ${styles[`level${level}`]}`}
            onClick={() =>
              hasChildren &&
              setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
            }
          >
            {hasChildren && (
              <span className={styles.arrow}>
                {expanded[id] ? "▼" : "▶"}
              </span>
            )}
            {key}
          </div>

          {hasChildren && expanded[id] && (
            <div className={styles.child}>
              {renderTree(value, level + 1, `${id}-`)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={styles.wrap}>
      <h2>(일반) 매출 계정과목 목록</h2>

      <div className={styles.tree}>
        {Object.keys(tree).length > 0 ? (
          renderTree(tree)
        ) : (
          <div style={{ padding: "10px", color: "#777" }}>
            계정과목 데이터가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
}
